import { Checkbox } from '@ordo/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Label } from '@ordo/ui/label'
import { Printer } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { CHIP_CLASS_SM, CHIP_CLASS_SM_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import { PreliminaryPrintConfirm } from '@/components/chefs/print/preliminary-print-confirm'
import { DayPills } from '@/components/print/day-pills'
import type { PacketPrintStatus } from '@/hooks/chefs/useKitchenPacketPrint'
import { anyPacketSection, PACKET_SECTION_DEFAULTS, type PacketSections } from '@/lib/chefs/packet/target'
import { isoDate, type ServiceDay } from '@/lib/chefs/service-days'

interface PrintDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Returns false when the print could not start yet — the dialog then stays open. */
  onPrint: (sections: PacketSections, selectedDates: Set<string>) => boolean | void
  /** Reports the current effective selection so the parent can measure it. */
  onSelectionChange: (sections: PacketSections, selectedDates: Set<string>) => void
  /** Measured packet size for the current selection, or null while measuring. */
  preview: { totalPages: number; dayCount: number } | null
  /** All service days in the current visible range (5 for weekly, 1 for daily). */
  serviceDays: ServiceDay<unknown>[]
  /** True when the range spans 2+ days (i.e. weekly view). */
  showDaySelector: boolean
  /**
   * Where the prepared print stands. Print is enabled only once it is `ready`;
   * its page count (the real PDF's) replaces the measured estimate. Omitted =
   * Print is enabled whenever something is selected.
   */
  status?: PacketPrintStatus
  /** Prepare again after a failure (or reload when the tab is outdated). */
  onRetry?: () => void
  /**
   * When the current selection's counts lock, while a school can still change
   * its order. Set → Print asks first and the packet prints watermarked
   * "INCOMPLETE ORDER" (PROD-1452). Null/omitted = final counts, prints directly.
   */
  preliminaryLocksAt?: string | null
}

// Matt's V2 packet (Oct 16) as Abner updated it (Oct 18) and Matt fine-tuned it (Oct 20), in packet order after each
// day's cover, which always prints. All on by default. Sheila, Oct 23: the three names only, no subtext; the kitchen
// copy stays on (`PACKET_SECTION_DEFAULTS`).
type DialogSectionKey = Exclude<keyof PacketSections, 'kitchenCopy'>

const DIALOG_SECTIONS: ReadonlyArray<{ key: DialogSectionKey; label: string }> = [
  { key: 'delivery', label: '1 · Delivery Sheets' },
  { key: 'production', label: '2 · Orders & Recipes' },
  { key: 'ordering', label: '3 · Shopping List' },
]

const SHORT_DAY_LABEL = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export function PrintDialog({
  open,
  onOpenChange,
  onPrint,
  onSelectionChange,
  preview,
  serviceDays,
  showDaySelector,
  status,
  onRetry,
  preliminaryLocksAt = null,
}: PrintDialogProps) {
  const [sections, setSections] = useState<PacketSections>(PACKET_SECTION_DEFAULTS)
  const [confirmingPreliminary, setConfirmingPreliminary] = useState(false)
  const allServingDates = useMemo(
    () => serviceDays.filter((d) => d.status !== 'none').map((d) => isoDate(d.date)),
    [serviceDays],
  )
  const [selectedDates, setSelectedDates] = useState<Set<string>>(() => new Set(allServingDates))

  useEffect(() => {
    if (open) {
      setSections(PACKET_SECTION_DEFAULTS)
      setSelectedDates(new Set(allServingDates))
      setConfirmingPreliminary(false)
    }
  }, [open, allServingDates])

  const toggleSection = (key: keyof PacketSections) => (checked: boolean | 'indeterminate') => {
    setSections((prev) => ({ ...prev, [key]: checked === true }))
  }

  const toggleDate = (iso: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev)
      if (next.has(iso)) {
        next.delete(iso)
      } else {
        next.add(iso)
      }
      return next
    })
  }

  const nothingSelected = !anyPacketSection(sections)
  const noDaysSelected = showDaySelector && selectedDates.size === 0
  const disabled = nothingSelected || noDaysSelected
  const notReady = status !== undefined && status.state !== 'ready'
  const printPages = status?.state === 'ready' && status.pages !== null ? status.pages : (preview?.totalPages ?? null)

  const effectiveDates = useMemo(
    () => (showDaySelector ? selectedDates : new Set(allServingDates)),
    [showDaySelector, selectedDates, allServingDates],
  )

  // Push the current selection up for measurement whenever it changes (and on open).
  useEffect(() => {
    if (open) {
      onSelectionChange(sections, effectiveDates)
    }
  }, [open, sections, effectiveDates, onSelectionChange])

  // Runs inside the tap that starts it (Print, or the confirm's "Print
  // preliminary copy"): the PDF share sheet needs that user activation.
  const handlePrint = () => {
    if (disabled || notReady) {
      return
    }
    if (onPrint(sections, effectiveDates) === false) {
      return
    }
    onOpenChange(false)
  }

  const requestPrint = () => {
    if (disabled || notReady) {
      return
    }
    if (preliminaryLocksAt) {
      setConfirmingPreliminary(true)
      return
    }
    handlePrint()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Print orders & recipes</DialogTitle>
          <DialogDescription className="sr-only">Choose the sections to print.</DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col divide-y divide-border">
          {DIALOG_SECTIONS.map(({ key, label }) => {
            const id = `print-section-${key}`
            return (
              <li key={key} className="flex items-center gap-3 py-3 first:pt-1">
                <Checkbox id={id} checked={sections[key]} onCheckedChange={toggleSection(key)} />
                <Label htmlFor={id} className="text-[15px] font-semibold leading-5 text-foreground">
                  {label}
                </Label>
              </li>
            )
          })}
        </ul>

        {showDaySelector ? (
          <div className="flex flex-col gap-2 pt-1">
            <p className="text-sm font-medium text-foreground">Days to print</p>
            <DayPills
              days={serviceDays.map((day) => {
                const iso = isoDate(day.date)
                return {
                  iso,
                  pillLabel: SHORT_DAY_LABEL[day.date.getDay()] ?? '??',
                  shortDateLabel: iso,
                  hasService: day.status !== 'none',
                  selected: selectedDates.has(iso),
                }
              })}
              onToggle={toggleDate}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-2">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {disabled ? (
              'Nothing selected'
            ) : status?.state === 'error' ? (
              <span className="text-destructive">{status.message}</span>
            ) : status?.state === 'preparing' ? (
              'Preparing the print…'
            ) : preview && printPages !== null ? (
              <>
                Print packet:{' '}
                <span className="font-medium text-foreground">
                  {printPages} page{printPages === 1 ? '' : 's'}
                </span>{' '}
                for {preview.dayCount} day{preview.dayCount === 1 ? '' : 's'}
              </>
            ) : (
              'Calculating pages…'
            )}
          </p>
          <div className="flex items-center gap-2">
            <ChipButton className={CHIP_CLASS_SM} onClick={() => onOpenChange(false)}>
              Cancel
            </ChipButton>
            {status?.state === 'error' && onRetry ? (
              <ChipButton variant="default" className={CHIP_CLASS_SM_PRIMARY} onClick={onRetry}>
                {status.action === 'reload' ? 'Reload' : 'Try again'}
              </ChipButton>
            ) : (
              <ChipButton
                variant="default"
                Icon={Printer}
                className={CHIP_CLASS_SM_PRIMARY}
                disabled={disabled || notReady}
                onClick={requestPrint}>
                {status?.state === 'preparing' ? 'Preparing…' : 'Print'}
              </ChipButton>
            )}
          </div>
        </div>
      </DialogContent>
      <PreliminaryPrintConfirm
        open={confirmingPreliminary}
        onOpenChange={setConfirmingPreliminary}
        locksAtLabel={preliminaryLocksAt}
        onConfirm={handlePrint}
      />
    </Dialog>
  )
}
